// ---------------
// FICHIER : src/pages/SMSDoc.js
// Documentation API SMS WonyaSoft
// ---------------

/**
 * SMSDoc - Documentation de l'API d'envoi de SMS
 * 
 * Cette API vous permet d'envoyer facilement des SMS de notification
 * pour vos applications de connexion (Login Apps). Elle est conçue pour être
 * simple à utiliser et hautement fiable.
 * 
 * Cas d'usage principaux :
 * - Codes OTP pour authentification à deux facteurs (2FA)
 * - Notifications de connexion (login/logout) avec détails de localisation
 * - Alertes de sécurité (tentatives de connexion suspectes)
 * - Codes de vérification pour inscription et confirmation d'identité
 * - Notifications de changement de mot de passe
 * - Confirmations de transactions sensibles
 * - Alertes en temps réel pour activités suspectes
 * - Messages de bienvenue personnalisés
 * - Rappels de session expirée
 * 
 * Avantages clés :
 * - Délivrabilité de 99.9% garantie
 * - Latence ultra-faible (<2 secondes)
 * - Support international complet
 * - API REST simple et sécurisée
 * - Traçabilité complète des envois
 * - Tarification transparente et compétitive
 * 
 * @component
 * @author WonyaSoft Team
 * @version 3.0
 * @since 2024
 */
import React, { useState, useEffect } from 'react';
import { API_PORTAL_URL } from '../constants/links';

const SMSDoc = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [showScrollButton, setShowScrollButton] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollButton(true);
            } else {
                setShowScrollButton(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            {/* Hero Section */}
            <section className="page-hero bulkpay-hero">
                <div className="page-hero-content">
                    <h1>📱 API SMS - Notifications pour vos Login Apps</h1>
                    <p>🚀 API simple et puissante pour envoyer des SMS de notification de connexion</p>
                    <div className="hero-badges">
                        <span className="badge badge-success">✅ Intégration en 2 minutes</span>
                        <span className="badge badge-primary">🔒 Authentification 2FA</span>
                        <span className="badge badge-warning">⚡ Livraison instantanée</span>
                        <span className="badge badge-info">📊 99.9% de délivrabilité</span>
                    </div>
                    <div className="hero-stats">
                        <div className="stat-item">
                            <span className="stat-number">{'<'}2s</span>
                            <span className="stat-label">Latence Moyenne</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">24/7</span>
                            <span className="stat-label">Disponibilité</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">190+</span>
                            <span className="stat-label">Pays Supportés</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Navigation Tabs */}
            <section className="doc-tabs">
                <div className="tabs-container">
                    <button 
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Vue d'ensemble
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'api' ? 'active' : ''}`}
                        onClick={() => setActiveTab('api')}
                    >
                        Documentation API
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'examples' ? 'active' : ''}`}
                        onClick={() => setActiveTab('examples')}
                    >
                        Exemples de code
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'errors' ? 'active' : ''}`}
                        onClick={() => setActiveTab('errors')}
                    >
                        Gestion des erreurs
                    </button>
                </div>
            </section>

            {/* Contenu des onglets */}
            <section className="tab-content">
                
                {/* Onglet Vue d'ensemble */}
                {activeTab === 'overview' && (
                    <div className="tab-panel">
                        {/* Section Pourquoi choisir notre API SMS */}
                        <div className="why-choose-section">
                            <h2 className="section-title">🎯 Pourquoi choisir notre API SMS pour vos Login Apps ?</h2>
                            <div className="benefits-grid">
                                <div className="benefit-card">
                                    <div className="benefit-icon">🔐</div>
                                    <h3>Authentification Ultra-Sécurisée</h3>
                                    <p>Envoyez des codes OTP et notifications de connexion avec une fiabilité de 99.9%. Protection avancée contre la fraude et les tentatives d'accès non autorisées.</p>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">⚡</div>
                                    <h3>Livraison Instantanée Garantie</h3>
                                    <p>Latence ultra-faible de moins de 2 secondes. Vos utilisateurs reçoivent leurs codes de vérification immédiatement, même aux heures de pointe.</p>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">🌍</div>
                                    <h3>Couverture Mondiale</h3>
                                    <p>Supportez vos utilisateurs dans plus de 190 pays avec des tarifs compétitifs et une qualité de service constante partout dans le monde.</p>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">📊</div>
                                    <h3>Analytiques Avancées</h3>
                                    <p>Suivi en temps réel des livraisons, rapports détaillés et métriques de performance pour optimiser votre taux de conversion.</p>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">💰</div>
                                    <h3>Tarification Transparente</h3>
                                    <p>Pas de frais cachés ! Payez uniquement pour les SMS envoyés avec des tarifs dégressifs selon votre volume d'utilisation.</p>
                                </div>
                                <div className="benefit-card">
                                    <div className="benefit-icon">🛠️</div>
                                    <h3>Intégration Simplifiée</h3>
                                    <p>API RESTful intuitive avec une seule requête POST. Compatible avec tous les langages de programmation et frameworks modernes.</p>
                                </div>
                            </div>
                        </div>

                        {/* Section Cas d'usage spécifiques */}
                        <div className="use-cases-section">
                            <h2 className="section-title">💡 Cas d'Usage Principaux pour Login Apps</h2>
                            <div className="use-cases-grid">
                                <div className="use-case-card">
                                    <h3>🔑 Codes OTP & 2FA</h3>
                                    <p>Envoyez des codes de vérification à usage unique pour l'authentification à deux facteurs. Sécurisez les connexions avec des codes expirables.</p>
                                </div>
                                <div className="use-case-card">
                                    <h3>🚨 Alertes de Connexion</h3>
                                    <p>Notifiez vos utilisateurs à chaque connexion : nouvel appareil, localisation inhabituelle, adresse IP suspecte avec géolocalisation.</p>
                                </div>
                                <div className="use-case-card">
                                    <h3>🔒 Notifications de Sécurité</h3>
                                    <p>Alertes immédiates pour changements de mot de passe, tentatives de connexion échouées, modifications de profil sensibles.</p>
                                </div>
                                <div className="use-case-card">
                                    <h3>✉️ Confirmations d'Inscription</h3>
                                    <p>Validez les nouveaux comptes avec des codes de confirmation SMS. Réduisez les inscriptions frauduleuses et les faux comptes.</p>
                                </div>
                                <div className="use-case-card">
                                    <h3>🔄 Récupération de Compte</h3>
                                    <p>Processus sécurisé de réinitialisation de mot de passe avec codes temporaires et liens d'expiration automatique.</p>
                                </div>
                                <div className="use-case-card">
                                    <h3>⏰ Rappels de Session</h3>
                                    <p>Notifications d'expiration de session, rappels de déconnexion automatique et invitations à renouveler l'authentification.</p>
                                </div>
                            </div>
                        </div>

                        {/* Section Métriques de Performance */}
                        <div className="performance-section">
                            <h2 className="section-title">📈 Performance & Fiabilité Exceptionnelles</h2>
                            <div className="metrics-grid">
                                <div className="metric-card">
                                    <div className="metric-number">99.9%</div>
                                    <div className="metric-label">Taux de Délivrabilité</div>
                                    <div className="metric-desc">Garantie de livraison même aux heures de pointe</div>
                                </div>
                                <div className="metric-card">
                                    <div className="metric-number">{'<'}2s</div>
                                    <div className="metric-label">Latence Moyenne</div>
                                    <div className="metric-desc">Réception instantanée pour une UX optimale</div>
                                </div>
                                <div className="metric-card">
                                    <div className="metric-number">24/7</div>
                                    <div className="metric-label">Disponibilité</div>
                                    <div className="metric-desc">Service ininterrompu avec redondance globale</div>
                                </div>
                                <div className="metric-card">
                                    <div className="metric-number">190+</div>
                                    <div className="metric-label">Pays Couverts</div>
                                    <div className="metric-desc">Portée mondiale pour vos utilisateurs</div>
                                </div>
                            </div>
                        </div>

                        <div className="overview-grid">
                            <div className="overview-card">
                                <h3>🎯 À quoi sert l'API SMS ?</h3>
                                <p>L'API SMS WonyaSoft permet d'envoyer des messages SMS programmables via une simple requête HTTP. Spécialement optimisée pour :</p>
                                <ul>
                                    <li>🔐 <strong>Codes OTP sécurisés</strong> - Authentification à deux facteurs fiable</li>
                                    <li>🚨 <strong>Alertes de connexion temps réel</strong> - Notifications de sécurité instantanées</li>
                                    <li>📱 <strong>Vérification de numéros</strong> - Validation d'identité par SMS</li>
                                    <li>🔒 <strong>Notifications de sécurité</strong> - Changements de compte importants</li>
                                    <li>⚡ <strong>Messages transactionnels</strong> - Confirmations et alertes système</li>
                                </ul>
                            </div>

                            <div className="overview-card">
                                <h3>⚡ Caractéristiques principales</h3>
                                <ul>
                                    <li><strong>🚀 API REST ultra-simple</strong> - Une seule requête POST pour envoyer</li>
                                    <li><strong>🔒 Sécurité renforcée</strong> - Token unique et chiffrement SSL/TLS</li>
                                    <li><strong>⚡ Envoi instantané</strong> - Livraison en moins de 2 secondes garanti</li>
                                    <li><strong>🏷️ Expéditeur personnalisé</strong> - Nom de marque reconnaissable</li>
                                    <li><strong>📊 Traçabilité complète</strong> - ID unique et statut de livraison</li>
                                    <li><strong>🌍 Support international</strong> - 190+ pays avec qualité optimale</li>
                                    <li><strong>💰 Tarifs compétitifs</strong> - À partir de 0.02 USD/SMS local</li>
                                </ul>
                            </div>

                            <div className="overview-card">
                                <h3>🔐 Prérequis</h3>
                                <p>Pour utiliser l'API SMS, vous devez disposer de :</p>
                                <ol>
                                    <li><strong>RefPartenaire</strong> - Votre référence partenaire unique (ex: "AA082")</li>
                                    <li><strong>TokenSMS</strong> - Votre token d'authentification SMS sécurisé</li>
                                    <li><strong>Crédits SMS</strong> - Solde suffisant sur votre compte WonyaSoft</li>
                                </ol>
                                <div className="info-highlight">
                                    💡 <strong>Accès instantané :</strong> Contactez notre équipe pour obtenir vos identifiants d'accès API en moins de 24h.
                                </div>
                            </div>

                            <div className="overview-card pricing-card">
                                <h3>📊 Tarification Transparente</h3>
                                <p>Tarifs ultra-compétitifs sans frais cachés :</p>
                                <div className="pricing-list">
                                    <div className="pricing-item">
                                        <span className="price">🇨🇩 0.02 USD</span>
                                        <span className="desc">SMS local RDC (par message)</span>
                                    </div>
                                    <div className="pricing-item">
                                        <span className="price">🌍 Variable</span>
                                        <span className="desc">SMS international selon destination</span>
                                    </div>
                                    <div className="pricing-item">
                                        <span className="price">✅ 0 USD</span>
                                        <span className="desc">Frais d'installation et abonnement</span>
                                    </div>
                                    <div className="pricing-item">
                                        <span className="price">📉 -30%</span>
                                        <span className="desc">Remise dégressive selon volume</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Onglet Documentation API */}
                {activeTab === 'api' && (
                    <div className="tab-panel">
                        <h2>📡 Documentation de l'API</h2>

                        <div className="api-section">
                            <h3>📡 Endpoint Principal</h3>
                            <div className="endpoint-info">
                                <div className="endpoint-badge">
                                    <span className="method-badge">POST</span>
                                    <span className="protocol-badge">HTTPS</span>
                                    <span className="format-badge">JSON</span>
                                </div>
                                <div className="code-block">
                                    <code>POST https://api.wonyasoft.com/sendsms</code>
                                </div>
                                <div className="endpoint-note">
                                    💡 <strong>Important :</strong> Cette API est optimisée pour les notifications de Login Apps avec une latence ultra-faible et une fiabilité maximale.
                                </div>
                            </div>
                        </div>

                        <div className="api-section">
                            <h3>🔧 Headers Requis</h3>
                            <div className="headers-info">
                                <div className="code-block">
                                    <pre>{`Content-Type: application/json
Authorization: Bearer {your-token} // Optionnel pour sécurité renforcée
User-Agent: YourApp/1.0 // Recommandé pour identification`}</pre>
                                </div>
                                <div className="security-note">
                                    🔒 <strong>Sécurité :</strong> Toujours utiliser HTTPS pour protéger vos tokens et données sensibles.
                                </div>
                            </div>
                        </div>

                        <div className="api-section">
                            <h3>📄 Paramètres du Body (JSON)</h3>
                            <div className="params-intro">
                                <p>💡 <strong>Optimisé pour Login Apps :</strong> Tous ces paramètres sont spécialement conçus pour maximiser la sécurité et la rapidité des notifications d'authentification.</p>
                            </div>
                            <table className="param-table">
                                <thead>
                                    <tr>
                                        <th>Paramètre</th>
                                        <th>Type</th>
                                        <th>Obligatoire</th>
                                        <th>Description</th>
                                        <th>Exemple Login App</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>RefPartenaire</code></td>
                                        <td>String</td>
                                        <td>✅ Oui</td>
                                        <td>Votre référence partenaire unique pour identification sécurisée</td>
                                        <td>"AA082" (Login MyApp)</td>
                                    </tr>
                                    <tr>
                                        <td><code>TokenSMS</code></td>
                                        <td>String</td>
                                        <td>✅ Oui</td>
                                        <td>Token d'authentification SMS crypté et sécurisé</td>
                                        <td>"VOTRE_TOKEN_SMS" (Hashé)</td>
                                    </tr>
                                    <tr>
                                        <td><code>MobileSMS</code></td>
                                        <td>String</td>
                                        <td>✅ Oui</td>
                                        <td>Numéro de téléphone avec indicatif pays (format international)</td>
                                        <td>"243819283848" (+243 RDC)</td>
                                    </tr>
                                    <tr>
                                        <td><code>SenderSMS</code></td>
                                        <td>String</td>
                                        <td>✅ Oui</td>
                                        <td>Nom de l'expéditeur (11 caractères max, alphanumérique)</td>
                                        <td>"MyApp" ou "Security"</td>
                                    </tr>
                                    <tr>
                                        <td><code>TextSMS</code></td>
                                        <td>String</td>
                                        <td>✅ Oui</td>
                                        <td>Contenu du SMS (160 caractères max, UTF-8 supporté)</td>
                                        <td>"Code OTP: 123456" ou "Connexion détectée"</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="params-tips">
                                <h4>📝 Conseils pour Login Apps :</h4>
                                <ul>
                                    <li><strong>SenderSMS</strong> : Utilisez le nom de votre app pour reconnaissance instantanée</li>
                                    <li><strong>TextSMS</strong> : Soyez concis et clair ("Code: 123456" plutôt qu'un long texte)</li>
                                    <li><strong>MobileSMS</strong> : Validez toujours le format avant envoi pour éviter les échecs</li>
                                    <li><strong>TokenSMS</strong> : Renouvelez régulièrement pour sécurité optimale</li>
                                </ul>
                            </div>
                        </div>

                        <div className="api-section">
                            <h3>💻 Exemple de Requête (Login App - Code OTP)</h3>
                            <div className="example-intro">
                                <p>🎯 <strong>Scénario :</strong> Envoi d'un code OTP pour authentification à deux facteurs lors de la connexion</p>
                            </div>
                            <div className="code-block">
                                <pre>{`{
  "RefPartenaire": "AA082",
  "TokenSMS": "VOTRE_TOKEN_SMS",
  "MobileSMS": "243819283848",
  "SenderSMS": "MyApp",
  "TextSMS": "Code de vérification MyApp: 752943. Valide 5min. Ne partagez jamais ce code."
}`}</pre>
                            </div>
                            <div className="example-variants">
                                <h4>🔄 Autres exemples pour Login Apps :</h4>
                                <div className="variant-examples">
                                    <div className="variant-example">
                                        <h5>🚨 Alerte de connexion :</h5>
                                        <div className="code-block small">
                                            <pre>{`"TextSMS": "Connexion détectée sur MyApp depuis Paris, France à 14:32. Si ce n'était pas vous, changez votre mot de passe."`}</pre>
                                        </div>
                                    </div>
                                    <div className="variant-example">
                                        <h5>✉️ Bienvenue nouvel utilisateur :</h5>
                                        <div className="code-block small">
                                            <pre>{`"TextSMS": "Bienvenue sur MyApp ! Votre compte a été créé avec succès. Code de confirmation: 456789"`}</pre>
                                        </div>
                                    </div>
                                    <div className="variant-example">
                                        <h5>🔒 Réinitialisation mot de passe :</h5>
                                        <div className="code-block small">
                                            <pre>{`"TextSMS": "Réinitialisation MyApp: Votre code temporaire est 891234. Valide 10 minutes."`}</pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="api-section">
                            <h3>Réponse Succès (200)</h3>
                            <div className="code-block success">
                                <pre>{`{
  "id": "000000000000000001",
  "status": "S",
  "MobileSMS": "243819284848"
}`}</pre>
                            </div>
                            <table className="param-table">
                                <thead>
                                    <tr>
                                        <th>Champ</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>id</code></td>
                                        <td>String</td>
                                        <td>Identifiant unique du SMS envoyé</td>
                                    </tr>
                                    <tr>
                                        <td><code>status</code></td>
                                        <td>String</td>
                                        <td>"S" = Succès, le SMS a été envoyé avec succès</td>
                                    </tr>
                                    <tr>
                                        <td><code>MobileSMS</code></td>
                                        <td>String</td>
                                        <td>Numéro du destinataire confirmé</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="api-section">
                            <h3>Réponses d'erreur</h3>
                            <div className="code-block error">
                                <pre>{`{
  "status": "E",
  "error": "Description de l'erreur",
  "code": "ERROR_CODE"
}`}</pre>
                            </div>
                            <p className="warning-box">
                                ⚠️ <strong>Important :</strong> Conservez l'ID de chaque SMS envoyé pour le suivi et le support technique.
                            </p>
                        </div>
                    </div>
                )}

                {/* Onglet Exemples de code */}
                {activeTab === 'examples' && (
                    <div className="tab-panel">
                        <h2>💻 Exemples de code (Production 2026)</h2>
                        
                        <div className="info-box" style={{ marginBottom: 30 }}>
                            <strong>🚀 Standards Production 2026 :</strong> Tous les exemples incluent gestion d'erreur complète, timeout, validation de réponse et logging structuré.
                        </div>

                        {/* PHP 8.2+ */}
                        <div className="code-example">
                            <h3>🐘 PHP 8.2+ (Strict, Try/Catch)</h3>
                            <div className="code-block">
                                <pre>{`<?php
declare(strict_types=1);

$url = 'https://api.wonyasoft.com/sendsms';

$payload = [
    'RefPartenaire' => 'AA082',
    'TokenSMS'      => 'VOTRE_TOKEN_SMS',
    'MobileSMS'     => '243819283848',
    'SenderSMS'     => 'MyApp',
    'TextSMS'       => 'Code de vérification MyApp: 752943. Valide 5min.'
];

$opts = [
    'http' => [
        'method'  => 'POST',
        'header'  => "Content-Type: application/json\\r\\n",
        'content' => json_encode($payload, JSON_THROW_ON_ERROR),
        'timeout' => 15,
    ]
];

try {
    $context = stream_context_create($opts);
    $response = file_get_contents($url, false, $context);
    
    if ($response === false) {
        throw new RuntimeException('HTTP request failed');
    }
    
    $data = json_decode($response, true, 512, JSON_THROW_ON_ERROR);
    
    if (isset($data['status']) && $data['status'] === 'S') {
        echo "SMS envoyé avec succès!\\n";
        echo "ID: " . ($data['id'] ?? 'N/A') . "\\n";
        echo "Destinataire: " . ($data['MobileSMS'] ?? 'N/A') . "\\n";
    } else {
        echo "Erreur SMS: " . ($data['error'] ?? 'Erreur inconnue') . "\\n";
        error_log('[SMS] Erreur: ' . ($data['code'] ?? 'N/A'));
    }
} catch (Throwable $e) {
    error_log('[SMS] Exception: ' . $e->getMessage());
    echo "Erreur technique: " . $e->getMessage();
}`}</pre>
                            </div>
                        </div>

                        {/* React Native / JS */}
                        <div className="code-example">
                            <h3>⚛️ React Native / JavaScript (Fetch, Async/Await)</h3>
                            <div className="code-block">
                                <pre>{`const url = 'https://api.wonyasoft.com/sendsms';

async function sendSMS() {
  const body = {
    RefPartenaire: 'AA082',
    TokenSMS: 'VOTRE_TOKEN_SMS',
    MobileSMS: '243819283848',
    SenderSMS: 'MyApp',
    TextSMS: 'Code de vérification MyApp: 752943. Valide 5min.'
  };

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/1.0'
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(\`HTTP \${res.status}: \${errText}\`);
    }

    const data = await res.json();
    
    if (data.status === 'S') {
      console.log('[SMS] Envoyé avec succès:', data.id);
      console.log('[SMS] Destinataire:', data.MobileSMS);
    } else {
      console.error('[SMS] Erreur:', data.error || 'Erreur inconnue');
      console.error('[SMS] Code:', data.code);
    }
  } catch (err) {
    console.error('[SMS] Exception:', err.message);
  }
}

sendSMS();`}</pre>
                            </div>
                        </div>

                        {/* Flutter / Dart */}
                        <div className="code-example">
                            <h3>🎯 Flutter / Dart (HTTP Package)</h3>
                            <div className="code-block">
                                <pre>{`import 'dart:convert';
import 'package:http/http.dart' as http;

Future<void> sendSMS() async {
  const url = 'https://api.wonyasoft.com/sendsms';

  final payload = {
    'RefPartenaire': 'AA082',
    'TokenSMS': 'VOTRE_TOKEN_SMS',
    'MobileSMS': '243819283848',
    'SenderSMS': 'MyApp',
    'TextSMS': 'Code de vérification MyApp: 752943. Valide 5min.'
  };

  try {
    final res = await http.post(
      Uri.parse(url),
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'MyApp/1.0'
      },
      body: jsonEncode(payload),
    ).timeout(Duration(seconds: 15));

    if (res.statusCode >= 200 && res.statusCode < 300) {
      final data = jsonDecode(res.body);
      
      if (data['status'] == 'S') {
        print('[SMS] Envoyé avec succès: \${data['id']}');
        print('[SMS] Destinataire: \${data['MobileSMS']}');
      } else {
        print('[SMS] Erreur: \${data['error'] ?? 'Erreur inconnue'}');
        print('[SMS] Code: \${data['code']}');
      }
    } else {
      throw Exception('HTTP \${res.statusCode}: \${res.body}');
    }
  } catch (e) {
	    print('[SMS] Exception: $e');
  }
}`}</pre>
                            </div>
                        </div>

                        {/* Python */}
                        <div className="code-example">
                            <h3>🐍 Python 3.11+ (Requests)</h3>
                            <div className="code-block">
                                <pre>{`import requests
from requests.exceptions import Timeout, ConnectionError

url = "https://api.wonyasoft.com/sendsms"

payload = {
    "RefPartenaire": "AA082",
    "TokenSMS": "VOTRE_TOKEN_SMS",
    "MobileSMS": "243819283848",
    "SenderSMS": "MyApp",
    "TextSMS": "Code de vérification MyApp: 752943. Valide 5min."
}

headers = {
    "Content-Type": "application/json",
    "User-Agent": "MyApp/1.0"
}

try:
    resp = requests.post(url, json=payload, headers=headers, timeout=15)
    resp.raise_for_status()
    data = resp.json()
    
    if data.get("status") == "S":
        print(f"[SMS] Envoyé avec succès: {data.get('id')}")
        print(f"[SMS] Destinataire: {data.get('MobileSMS')}")
    else:
        print(f"[SMS] Erreur: {data.get('error', 'Erreur inconnue')}")
        print(f"[SMS] Code: {data.get('code')}")
        
except Timeout:
    print("[SMS] Timeout sur la requête")
except ConnectionError:
    print("[SMS] Erreur de connexion réseau")
except requests.HTTPError as e:
    print(f"[SMS] HTTP {resp.status_code}: {resp.text}")
except Exception as e:
    print(f"[SMS] Exception: {e}")`}</pre>
                            </div>
                        </div>

                        {/* Windev */}
                        <div className="code-example">
                            <h3>🔧 Windev (WLangage)</h3>
                            <div className="code-block">
                                <pre>{`sURL is string = "https://api.wonyasoft.com/sendsms"

req is httpRequest
req..URL = sURL
req..Method = httpPost
req..ContentType = "application/json"
req..Header["User-Agent"] = "MyApp/1.0"

payload is string = [
{
 "RefPartenaire": "AA082",
 "TokenSMS": "VOTRE_TOKEN_SMS",
 "MobileSMS": "243819283848",
 "SenderSMS": "MyApp",
 "TextSMS": "Code de vérification MyApp: 752943. Valide 5min."
}
]

req..Content = payload

rep is httpResponse = HTTPSend(req)

IF rep..StatusCode >= 200 AND rep..StatusCode < 300 THEN
    // Parser JSON
    jData is JSON = JSONToVariant(rep..Content)
    
    IF jData.status = "S" THEN
        Info("[SMS] Envoyé avec succès : " + jData.id)
        Trace("[SMS] Destinataire : " + jData.MobileSMS)
    ELSE
        Error("[SMS] Erreur : " + jData.error)
        Trace("[SMS] Code erreur : " + jData.code)
    END
ELSE
    Error("[SMS] HTTP " + rep..StatusCode + " : " + rep..Content)
END`}</pre>
                            </div>
                        </div>

                        {/* cURL */}
                        <div className="code-example">
                            <h3>🖥️ cURL (Terminal)</h3>
                            <div className="code-block">
                                <pre>{`curl -X POST "https://api.wonyasoft.com/sendsms" \\
  -H "Content-Type: application/json" \\
  -H "User-Agent: MyApp/1.0" \\
  --connect-timeout 15 \\
  --max-time 30 \\
  -d '{
    "RefPartenaire": "AA082",
    "TokenSMS": "VOTRE_TOKEN_SMS",
    "MobileSMS": "243819283848",
    "SenderSMS": "MyApp",
    "TextSMS": "Code de vérification MyApp: 752943. Valide 5min."
}'`}</pre>
                            </div>
                        </div>

                        <div className="info-box" style={{ marginTop: 40 }}>
                            <h4 style={{ marginTop: 0, color: '#dc3545' }}>💡 Points clés des exemples production 2026</h4>
                            <ul style={{ marginBottom: 0, lineHeight: 1.8 }}>
                                <li>✅ <strong>Timeout configuré</strong> : 15 secondes pour éviter les blocages</li>
                                <li>✅ <strong>Gestion d'erreur complète</strong> : Try/catch avec exceptions spécifiques</li>
                                <li>✅ <strong>Validation de réponse</strong> : Vérification du status "S" pour succès</li>
                                <li>✅ <strong>Logging structuré</strong> : Préfixe [SMS] pour traçabilité</li>
                                <li>✅ <strong>Headers complets</strong> : Content-Type + User-Agent</li>
                                <li>✅ <strong>Typage strict</strong> : PHP avec declare(strict_types=1)</li>
                            </ul>
                        </div>

                        <div className="code-example">
                            <h3>JavaScript (Fetch API)</h3>
                            <div className="code-block">
                                <pre>{`const sendSMS = async () => {
  try {
    const response = await fetch('https://api.wonyasoft.com/sendsms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        RefPartenaire: "AA082",
        TokenSMS: "VOTRE_TOKEN_SMS",
        MobileSMS: "243819283848",
        SenderSMS: "Info",
        TextSMS: "Votre code de vérification est : 123456"
      })
    });

    const data = await response.json();
    
    if (response.ok && data.status === 'S') {
      console.log('SMS envoyé avec succès!');
      console.log('ID:', data.id);
      console.log('Destinataire:', data.MobileSMS);
    } else {
      console.error('Erreur:', data.error);
    }
  } catch (error) {
    console.error('Erreur réseau:', error);
  }
};

sendSMS();`}</pre>
                            </div>
                        </div>

                        <div className="code-example">
                            <h3>Node.js (Axios)</h3>
                            <div className="code-block">
                                <pre>{`const axios = require('axios');

async function sendSMS() {
  try {
    const response = await axios.post('https://api.wonyasoft.com/sendsms', {
      RefPartenaire: "AA082",
      TokenSMS: "VOTRE_TOKEN_SMS",
      MobileSMS: "243819283848",
      SenderSMS: "Info",
      TextSMS: "Votre code de vérification est : 123456"
    });

    if (response.data.status === 'S') {
      console.log('SMS envoyé avec succès!');
      console.log('ID:', response.data.id);
    }
  } catch (error) {
    console.error('Erreur:', error.response?.data || error.message);
  }
}

sendSMS();`}</pre>
                            </div>
                        </div>

                        <div className="code-example">
                            <h3>Python (Requests)</h3>
                            <div className="code-block">
                                <pre>{`import requests
import json

def send_sms():
    url = "https://api.wonyasoft.com/sendsms"
    
    payload = {
        "RefPartenaire": "AA082",
        "TokenSMS": "VOTRE_TOKEN_SMS",
        "MobileSMS": "243819283848",
        "SenderSMS": "Info",
        "TextSMS": "Votre code de vérification est : 123456"
    }
    
    headers = {
        "Content-Type": "application/json"
    }
    
    try:
        response = requests.post(url, json=payload, headers=headers)
        data = response.json()
        
        if response.status_code == 200 and data.get('status') == 'S':
            print(f"SMS envoyé avec succès!")
            print(f"ID: {data.get('id')}")
            print(f"Destinataire: {data.get('MobileSMS')}")
        else:
            print(f"Erreur: {data.get('error')}")
    except Exception as e:
        print(f"Erreur réseau: {str(e)}")

send_sms()`}</pre>
                            </div>
                        </div>

                        <div className="code-example">
                            <h3>PHP (cURL)</h3>
                            <div className="code-block">
                                <pre>{`<?php

function sendSMS() {
    $url = "https://api.wonyasoft.com/sendsms";
    
    $data = array(
        "RefPartenaire" => "AA082",
        "TokenSMS" => "VOTRE_TOKEN_SMS",
        "MobileSMS" => "243819283848",
        "SenderSMS" => "Info",
        "TextSMS" => "Votre code de vérification est : 123456"
    );
    
    $ch = curl_init($url);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
    curl_setopt($ch, CURLOPT_HTTPHEADER, array(
        'Content-Type: application/json'
    ));
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 200 && $result['status'] == 'S') {
        echo "SMS envoyé avec succès!\\n";
        echo "ID: " . $result['id'] . "\\n";
        echo "Destinataire: " . $result['MobileSMS'] . "\\n";
    } else {
        echo "Erreur: " . $result['error'] . "\\n";
    }
}

sendSMS();

?>`}</pre>
                            </div>
                        </div>

                        <div className="code-example">
                            <h3>Java (HttpURLConnection)</h3>
                            <div className="code-block">
                                <pre>{`import java.io.*;
import java.net.*;
import org.json.*;

public class SMSSender {
    public static void sendSMS() throws Exception {
        String url = "https://api.wonyasoft.com/sendsms";
        
        JSONObject payload = new JSONObject();
        payload.put("RefPartenaire", "AA082");
        payload.put("TokenSMS", "VOTRE_TOKEN_SMS");
        payload.put("MobileSMS", "243819283848");
        payload.put("SenderSMS", "Info");
        payload.put("TextSMS", "Votre code de vérification est : 123456");
        
        HttpURLConnection conn = (HttpURLConnection) new URL(url).openConnection();
        conn.setRequestMethod("POST");
        conn.setRequestProperty("Content-Type", "application/json");
        conn.setDoOutput(true);
        
        try(OutputStream os = conn.getOutputStream()) {
            byte[] input = payload.toString().getBytes("utf-8");
            os.write(input, 0, input.length);
        }
        
        try(BufferedReader br = new BufferedReader(
                new InputStreamReader(conn.getInputStream(), "utf-8"))) {
            StringBuilder response = new StringBuilder();
            String responseLine;
            while ((responseLine = br.readLine()) != null) {
                response.append(responseLine.trim());
            }
            
            JSONObject result = new JSONObject(response.toString());
            if (result.getString("status").equals("S")) {
                System.out.println("SMS envoyé avec succès!");
                System.out.println("ID: " + result.getString("id"));
            }
        }
    }
}`}</pre>
                            </div>
                        </div>

                        <div className="info-box">
                            💡 <strong>Conseil :</strong> Stockez vos identifiants (RefPartenaire et TokenSMS) dans des variables d'environnement pour plus de sécurité.
                        </div>
                    </div>
                )}

                {/* Onglet Gestion des erreurs */}
                {activeTab === 'errors' && (
                    <div className="tab-panel">
                        <h2>⚠️ Gestion des erreurs</h2>

                        <div className="error-section">
                            <h3>Codes d'erreur courants</h3>
                            <table className="error-table">
                                <thead>
                                    <tr>
                                        <th>Code HTTP</th>
                                        <th>Status</th>
                                        <th>Description</th>
                                        <th>Solution</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>400</td>
                                        <td>E</td>
                                        <td>Paramètres manquants ou invalides</td>
                                        <td>Vérifiez que tous les champs requis sont présents et au bon format</td>
                                    </tr>
                                    <tr>
                                        <td>401</td>
                                        <td>E</td>
                                        <td>Token invalide ou expiré</td>
                                        <td>Vérifiez votre TokenSMS et RefPartenaire</td>
                                    </tr>
                                    <tr>
                                        <td>402</td>
                                        <td>E</td>
                                        <td>Crédit insuffisant</td>
                                        <td>Rechargez votre compte SMS</td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>E</td>
                                        <td>Accès refusé</td>
                                        <td>Vérifiez vos permissions ou contactez le support</td>
                                    </tr>
                                    <tr>
                                        <td>422</td>
                                        <td>E</td>
                                        <td>Numéro de téléphone invalide</td>
                                        <td>Vérifiez le format du numéro (ex: 243819283848)</td>
                                    </tr>
                                    <tr>
                                        <td>429</td>
                                        <td>E</td>
                                        <td>Trop de requêtes</td>
                                        <td>Réduisez la fréquence des envois</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>E</td>
                                        <td>Erreur serveur</td>
                                        <td>Réessayez dans quelques minutes ou contactez le support</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="best-practices">
                            <h3>📋 Bonnes pratiques</h3>
                            <div className="practice-grid">
                                <div className="practice-card">
                                    <h4>✅ Validation des numéros</h4>
                                    <ul>
                                        <li>Toujours inclure l'indicatif pays (ex: 243 pour la RDC)</li>
                                        <li>Vérifier le format avant l'envoi</li>
                                        <li>Supprimer les espaces et caractères spéciaux</li>
                                        <li>Valider que le numéro contient uniquement des chiffres</li>
                                    </ul>
                                </div>

                                <div className="practice-card">
                                    <h4>🔄 Gestion des retry</h4>
                                    <ul>
                                        <li>Implémenter un système de retry avec backoff exponentiel</li>
                                        <li>Maximum 3 tentatives recommandé</li>
                                        <li>Attendre 1s, puis 2s, puis 4s entre les tentatives</li>
                                        <li>Logger tous les échecs pour analyse</li>
                                    </ul>
                                </div>

                                <div className="practice-card">
                                    <h4>📊 Logging et monitoring</h4>
                                    <ul>
                                        <li>Enregistrer tous les ID de SMS envoyés</li>
                                        <li>Logger les erreurs avec timestamp</li>
                                        <li>Monitorer le taux de succès</li>
                                        <li>Suivre la consommation de crédits</li>
                                    </ul>
                                </div>

                                <div className="practice-card">
                                    <h4>🔐 Sécurité</h4>
                                    <ul>
                                        <li>Ne jamais exposer vos tokens dans le code frontend</li>
                                        <li>Utiliser des variables d'environnement</li>
                                        <li>Effectuer les appels API depuis le backend</li>
                                        <li>Renouveler régulièrement vos tokens</li>
                                    </ul>
                                </div>

                                <div className="practice-card">
                                    <h4>📝 Contenu des messages</h4>
                                    <ul>
                                        <li>Limiter à 160 caractères par segment</li>
                                        <li>Éviter les caractères spéciaux non-ASCII</li>
                                        <li>Être clair et concis</li>
                                        <li>Inclure un nom d'expéditeur reconnaissable</li>
                                    </ul>
                                </div>

                                <div className="practice-card">
                                    <h4>⚡ Performance</h4>
                                    <ul>
                                        <li>Utiliser des pools de connexions pour les envois en masse</li>
                                        <li>Implémenter une file d'attente pour les grands volumes</li>
                                        <li>Respecter les limites de taux (rate limits)</li>
                                        <li>Utiliser des timeouts appropriés (30s recommandé)</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="warning-box">
                            ⚠️ <strong>Attention :</strong> Les SMS non délivrés (numéros invalides, hors service, etc.) consomment des crédits. Validez toujours les numéros avant l'envoi.
                        </div>

                        <div className="faq-section">
                            <h3>❓ FAQ</h3>
                            <div className="faq-item">
                                <h4>Q: Combien de temps faut-il pour qu'un SMS soit délivré ?</h4>
                                <p>R: En moyenne, les SMS sont délivrés en 1-5 secondes. Dans de rares cas, cela peut prendre jusqu'à 30 secondes.</p>
                            </div>
                            <div className="faq-item">
                                <h4>Q: Puis-je envoyer des SMS internationaux ?</h4>
                                <p>R: Oui, notre API supporte l'envoi international. Les tarifs varient selon la destination.</p>
                            </div>
                            <div className="faq-item">
                                <h4>Q: Quelle est la longueur maximale d'un SMS ?</h4>
                                <p>R: 160 caractères pour un SMS simple. Au-delà, le message est divisé en plusieurs segments facturés séparément.</p>
                            </div>
                            <div className="faq-item">
                                <h4>Q: Puis-je personnaliser le nom de l'expéditeur ?</h4>
                                <p>R: Oui, via le paramètre SenderSMS (11 caractères alphanumériques maximum).</p>
                            </div>
                            <div className="faq-item">
                                <h4>Q: Comment obtenir mes identifiants d'API ?</h4>
                                <p>R: Contactez notre équipe commerciale ou support technique à <a href="mailto:info@wonyasoft.com">info@wonyasoft.com</a></p>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Bouton retour en haut */}
            {showScrollButton && (
                <button className="scroll-to-top" onClick={scrollToTop}>
                    ↑
                </button>
            )}

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-content">
                    <h2>Prêt à commencer ?</h2>
                    <p>Contactez-nous pour obtenir vos identifiants d'API et commencer à envoyer des SMS</p>
                    <div className="cta-buttons">
                        <a href={API_PORTAL_URL} className="btn btn-primary">Commencer</a>
                        <a href={API_PORTAL_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">Autres APIs</a>
                    </div>
                </div>
            </section>

            <style jsx>{`
                .page-hero.bulkpay-hero {
                    background: linear-gradient(135deg, #dc3545 0%, #c0392b 100%);
                    color: white;
                    padding: 80px 20px;
                    text-align: center;
                }

                .page-hero-content h1 {
                    font-size: 3rem;
                    margin-bottom: 20px;
                    font-weight: 700;
                }

                .page-hero-content p {
                    font-size: 1.3rem;
                    margin-bottom: 30px;
                    opacity: 0.95;
                }

                .hero-badges {
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                    flex-wrap: wrap;
                    margin-bottom: 40px;
                }

                .badge {
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #fff;
                }

                .badge-success {
                    background: linear-gradient(135deg, #28a745, #20c997);
                    box-shadow: 0 3px 10px rgba(40,167,69,0.3);
                }

                .badge-primary {
                    background: linear-gradient(135deg, #007bff, #0056b3);
                    box-shadow: 0 3px 10px rgba(0,123,255,0.3);
                }

                .badge-warning {
                    background: linear-gradient(135deg, #fd7e14, #e8590c);
                    box-shadow: 0 3px 10px rgba(253,126,20,0.3);
                }

                .badge-info {
                    background: linear-gradient(135deg, #6f42c1, #5a32a3);
                    box-shadow: 0 3px 10px rgba(111,66,193,0.3);
                }

                .hero-stats {
                    display: flex;
                    justify-content: center;
                    gap: 50px;
                    flex-wrap: wrap;
                }

                .stat-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-number {
                    font-size: 2.5rem;
                    font-weight: 700;
                    display: block;
                }

                .stat-label {
                    font-size: 0.95rem;
                    opacity: 0.9;
                    margin-top: 5px;
                }

                .doc-tabs {
                    background: #f8f9fa;
                    padding: 20px 0;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .tabs-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    justify-content: center;
                    gap: 10px;
                    flex-wrap: wrap;
                    padding: 0 20px;
                }

                .tab-btn {
                    padding: 12px 30px;
                    background: white;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    font-size: 1rem;
                }

                .tab-btn:hover {
                    border-color: #dc3545;
                    color: #dc3545;
                    transform: translateY(-2px);
                }

                .tab-btn.active {
                    background: #dc3545;
                    color: white;
                    border-color: #dc3545;
                }

                .tab-content {
                    max-width: 1200px;
                    margin: 40px auto;
                    padding: 0 20px;
                }

                .tab-panel {
                    animation: fadeIn 0.5s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .overview-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 30px;
                    margin-top: 30px;
                }

                .overview-card {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }

                .overview-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 12px rgba(0,0,0,0.15);
                }

                .overview-card h3 {
                    color: #dc3545;
                    margin-bottom: 15px;
                    font-size: 1.4rem;
                }

                .overview-card ul, .overview-card ol {
                    margin: 15px 0;
                    padding-left: 20px;
                }

                .overview-card li {
                    margin: 10px 0;
                    line-height: 1.6;
                }

                .info-highlight {
                    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                    border-left: 4px solid #2196f3;
                    padding: 15px 20px;
                    margin: 20px 0;
                    border-radius: 8px;
                    color: #0d47a1;
                    font-weight: 500;
                    line-height: 1.6;
                }

                .pricing-card {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
                    border: 2px solid #28a745;
                }

                .pricing-list {
                    margin-top: 20px;
                }

                .pricing-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 0;
                    border-bottom: 1px solid #e0e0e0;
                }

                .pricing-item:last-child {
                    border-bottom: none;
                }

                .pricing-item .price {
                    font-weight: 700;
                    color: #dc3545;
                    font-size: 1.1rem;
                }

                .pricing-item .desc {
                    color: #6c757d;
                    font-size: 0.95rem;
                }

                /* Nouvelles sections d'amélioration */
                .section-title {
                    text-align: center;
                    color: #dc3545;
                    font-size: 2.2rem;
                    margin-bottom: 40px;
                    font-weight: 700;
                }

                .why-choose-section {
                    margin: 50px 0;
                    padding: 50px 30px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 20px;
                    color: #fff;
                }

                .why-choose-section .section-title {
                    color: #fff;
                }

                .benefits-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 25px;
                }

                .benefit-card {
                    background: rgba(255,255,255,0.15);
                    padding: 30px;
                    border-radius: 16px;
                    text-align: center;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                    transition: transform 0.3s ease;
                }

                .benefit-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 30px rgba(0,0,0,0.2);
                }

                .benefit-icon {
                    font-size: 3.5rem;
                    margin-bottom: 20px;
                    display: block;
                }

                .benefit-card h3 {
                    color: #fff;
                    margin-bottom: 15px;
                    font-size: 1.4rem;
                    font-weight: 700;
                }

                .benefit-card p {
                    color: rgba(255,255,255,0.95);
                    line-height: 1.6;
                }

                .use-cases-section {
                    margin: 50px 0;
                    padding: 40px 0;
                }

                .use-cases-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 20px;
                }

                .use-case-card {
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                    padding: 25px;
                    border-radius: 16px;
                    color: #fff;
                    box-shadow: 0 8px 24px rgba(240,147,251,0.3);
                    transition: transform 0.3s ease;
                }

                .use-case-card:hover {
                    transform: translateY(-5px);
                }

                .use-case-card:nth-child(2) {
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                    box-shadow: 0 8px 24px rgba(79,172,254,0.3);
                }

                .use-case-card:nth-child(3) {
                    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
                    box-shadow: 0 8px 24px rgba(250,112,154,0.3);
                }

                .use-case-card:nth-child(4) {
                    background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
                    box-shadow: 0 8px 24px rgba(48,207,208,0.3);
                }

                .use-case-card:nth-child(5) {
                    background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
                    color: #2c3e50;
                    box-shadow: 0 8px 24px rgba(168,237,234,0.3);
                }

                .use-case-card:nth-child(6) {
                    background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
                    color: #2c3e50;
                    box-shadow: 0 8px 24px rgba(255,236,210,0.3);
                }

                .use-case-card h3 {
                    font-size: 1.3rem;
                    margin-bottom: 15px;
                    font-weight: 700;
                }

                .use-case-card p {
                    line-height: 1.6;
                    opacity: 0.95;
                }

                .performance-section {
                    margin: 50px 0;
                    padding: 40px 30px;
                    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
                    border-radius: 20px;
                    color: #fff;
                }

                .performance-section .section-title {
                    color: #fff;
                }

                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 25px;
                }

                .metric-card {
                    background: rgba(255,255,255,0.1);
                    padding: 30px;
                    border-radius: 16px;
                    text-align: center;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                }

                .metric-number {
                    font-size: 3rem;
                    font-weight: 700;
                    color: #3498db;
                    display: block;
                    margin-bottom: 10px;
                }

                .metric-label {
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin-bottom: 8px;
                    display: block;
                }

                .metric-desc {
                    font-size: 0.9rem;
                    opacity: 0.8;
                    line-height: 1.4;
                }

                .api-section {
                    margin: 30px 0;
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .api-section h3 {
                    color: #dc3545;
                    margin-bottom: 20px;
                    font-size: 1.5rem;
                }

                .endpoint-info, .headers-info {
                    background: #f8f9fa;
                    padding: 20px;
                    border-radius: 12px;
                    border: 1px solid #e0e0e0;
                }

                .endpoint-badge {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 15px;
                    flex-wrap: wrap;
                }

                .method-badge {
                    background: #dc3545;
                    color: #fff;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .protocol-badge {
                    background: #28a745;
                    color: #fff;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .format-badge {
                    background: #007bff;
                    color: #fff;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .endpoint-note, .security-note {
                    background: rgba(33, 150, 243, 0.1);
                    border-left: 4px solid #2196f3;
                    padding: 12px 16px;
                    margin-top: 15px;
                    border-radius: 6px;
                    color: #1565c0;
                    font-size: 0.95rem;
                    line-height: 1.5;
                }

                .params-intro {
                    background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
                    padding: 15px 20px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border-left: 4px solid #4caf50;
                }

                .params-intro p {
                    margin: 0;
                    color: #2e7d32;
                    font-weight: 500;
                }

                .params-tips {
                    background: #fff3e0;
                    padding: 20px;
                    border-radius: 8px;
                    margin-top: 20px;
                    border-left: 4px solid #ff9800;
                }

                .params-tips h4 {
                    color: #e65100;
                    margin-bottom: 15px;
                    font-size: 1.1rem;
                }

                .params-tips ul {
                    margin: 0;
                    padding-left: 20px;
                }

                .params-tips li {
                    margin: 8px 0;
                    color: #bf360c;
                    line-height: 1.5;
                }

                .example-intro {
                    background: linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%);
                    padding: 15px 20px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border-left: 4px solid #03a9f4;
                }

                .example-intro p {
                    margin: 0;
                    color: #0277bd;
                    font-weight: 500;
                }

                .example-variants {
                    margin-top: 30px;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 12px;
                    border: 1px solid #e0e0e0;
                }

                .example-variants h4 {
                    color: #dc3545;
                    margin-bottom: 20px;
                    font-size: 1.2rem;
                }

                .variant-examples {
                    display: grid;
                    gap: 20px;
                }

                .variant-example {
                    background: white;
                    padding: 20px;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }

                .variant-example h5 {
                    color: #6c757d;
                    margin-bottom: 10px;
                    font-size: 1rem;
                    font-weight: 600;
                }

                .code-block.small {
                    margin: 0;
                    padding: 15px;
                    font-size: 0.85rem;
                }

                .code-block {
                    background: #2d2d2d;
                    color: #f8f8f2;
                    padding: 20px;
                    border-radius: 8px;
                    overflow-x: auto;
                    margin: 15px 0;
                    font-family: 'Courier New', monospace;
                }

                .code-block.success {
                    border-left: 4px solid #4caf50;
                }

                .code-block.error {
                    border-left: 4px solid #f44336;
                }

                .code-block pre, .code-block code {
                    margin: 0;
                    padding: 0;
                    background: transparent;
                    color: inherit;
                    font-size: 0.95rem;
                    line-height: 1.6;
                }

                .param-table, .error-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 20px 0;
                    background: white;
                }

                .param-table th, .error-table th {
                    background: #dc3545;
                    color: white;
                    padding: 15px;
                    text-align: left;
                    font-weight: 600;
                }

                .param-table td, .error-table td {
                    padding: 15px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .param-table code, .error-table code {
                    background: #f5f5f5;
                    padding: 3px 8px;
                    border-radius: 4px;
                    color: #d63384;
                    font-size: 0.9rem;
                }

                .code-example {
                    margin: 40px 0;
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .code-example h3 {
                    color: #dc3545;
                    margin-bottom: 20px;
                    font-size: 1.4rem;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .info-box {
                    background: #e3f2fd;
                    border-left: 4px solid #2196f3;
                    padding: 15px 20px;
                    margin: 20px 0;
                    border-radius: 4px;
                    line-height: 1.6;
                }

                .warning-box {
                    background: #fff3cd;
                    border-left: 4px solid #ffc107;
                    padding: 15px 20px;
                    margin: 20px 0;
                    border-radius: 4px;
                    line-height: 1.6;
                }

                .error-section {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin: 30px 0;
                }

                .error-section h3 {
                    color: #dc3545;
                    margin-bottom: 20px;
                    font-size: 1.5rem;
                }

                .best-practices {
                    margin: 40px 0;
                }

                .best-practices h3 {
                    color: #dc3545;
                    margin-bottom: 30px;
                    font-size: 1.8rem;
                    text-align: center;
                }

                .practice-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 25px;
                }

                .practice-card {
                    background: white;
                    padding: 25px;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .practice-card h4 {
                    color: #dc3545;
                    margin-bottom: 15px;
                    font-size: 1.2rem;
                }

                .practice-card ul {
                    list-style: none;
                    padding: 0;
                }

                .practice-card li {
                    padding: 8px 0;
                    padding-left: 25px;
                    position: relative;
                    line-height: 1.6;
                }

                .practice-card li:before {
                    content: "→";
                    position: absolute;
                    left: 0;
                    color: #dc3545;
                    font-weight: bold;
                }

                .faq-section {
                    margin: 40px 0;
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .faq-section h3 {
                    color: #dc3545;
                    margin-bottom: 30px;
                    font-size: 1.8rem;
                }

                .faq-item {
                    margin: 25px 0;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border-left: 4px solid #dc3545;
                }

                .faq-item h4 {
                    color: #333;
                    margin-bottom: 10px;
                    font-size: 1.1rem;
                }

                .faq-item p {
                    color: #555;
                    line-height: 1.6;
                    margin: 0;
                }

                .faq-item a {
                    color: #dc3545;
                    text-decoration: none;
                }

                .faq-item a:hover {
                    text-decoration: underline;
                }

                .scroll-to-top {
                    position: fixed;
                    bottom: 30px;
                    right: 30px;
                    background: #dc3545;
                    color: white;
                    border: none;
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    font-size: 1.5rem;
                    cursor: pointer;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.2);
                    transition: all 0.3s ease;
                    z-index: 1000;
                }

                .scroll-to-top:hover {
                    background: #c0392b;
                    transform: translateY(-5px);
                    box-shadow: 0 6px 8px rgba(0,0,0,0.3);
                }

                .cta-section {
                    background: linear-gradient(135deg, #dc3545 0%, #c0392b 100%);
                    color: white;
                    padding: 60px 20px;
                    text-align: center;
                    margin-top: 60px;
                }

                .cta-content h2 {
                    font-size: 2.5rem;
                    margin-bottom: 20px;
                }

                .cta-content p {
                    font-size: 1.2rem;
                    margin-bottom: 30px;
                    opacity: 0.95;
                }

                .cta-buttons {
                    display: flex;
                    gap: 20px;
                    justify-content: center;
                    flex-wrap: wrap;
                }

                .btn {
                    padding: 15px 40px;
                    border-radius: 8px;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 1.1rem;
                    transition: all 0.3s ease;
                    display: inline-block;
                }

                .btn-primary {
                    background: white;
                    color: #dc3545;
                }

                .btn-primary:hover {
                    background: #f8f9fa;
                    transform: translateY(-3px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                }

                .btn-secondary {
                    background: transparent;
                    color: white;
                    border: 2px solid white;
                }

                .btn-secondary:hover {
                    background: white;
                    color: #dc3545;
                    transform: translateY(-3px);
                }

                @media (max-width: 768px) {
                    .page-hero-content h1 {
                        font-size: 2rem;
                    }

                    .page-hero-content p {
                        font-size: 1.1rem;
                    }

                    .hero-stats {
                        gap: 30px;
                    }

                    .hero-badges {
                        gap: 8px;
                    }

                    .badge {
                        padding: 6px 12px;
                        font-size: 0.8rem;
                    }

                    .stat-number {
                        font-size: 2rem;
                    }

                    .section-title {
                        font-size: 1.6rem;
                    }

                    .benefits-grid, .use-cases-grid, .metrics-grid {
                        grid-template-columns: 1fr;
                        gap: 15px;
                    }

                    .benefit-card, .use-case-card, .metric-card {
                        padding: 20px;
                    }

                    .benefit-icon {
                        font-size: 2.5rem;
                        margin-bottom: 15px;
                    }

                    .metric-number {
                        font-size: 2.2rem;
                    }

                    .why-choose-section, .performance-section {
                        padding: 30px 20px;
                        margin: 30px 0;
                    }

                    .info-highlight {
                        padding: 12px 15px;
                        font-size: 0.9rem;
                    }

                    .pricing-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 5px;
                    }

                    .pricing-item .price {
                        font-size: 1rem;
                    }

                    .pricing-item .desc {
                        font-size: 0.9rem;
                    }

                    .endpoint-badge {
                        justify-content: center;
                    }

                    .method-badge, .protocol-badge, .format-badge {
                        font-size: 0.7rem;
                        padding: 5px 10px;
                    }

                    .params-intro, .example-intro {
                        padding: 12px 15px;
                    }

                    .params-tips {
                        padding: 15px;
                    }

                    .param-table {
                        font-size: 0.85rem;
                    }

                    .param-table th, .param-table td {
                        padding: 10px 8px;
                    }

                    .variant-examples {
                        grid-template-columns: 1fr;
                    }

                    .variant-example {
                        padding: 15px;
                    }

                    .tabs-container {
                        overflow-x: auto;
                    }

                    .tab-btn {
                        padding: 10px 20px;
                        font-size: 0.9rem;
                    }

                    .overview-grid, .practice-grid {
                        grid-template-columns: 1fr;
                    }

                    .code-block {
                        font-size: 0.85rem;
                    }

                    .cta-content h2 {
                        font-size: 1.8rem;
                    }

                    .cta-buttons {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .btn {
                        padding: 12px 30px;
                    }
                }
            `}</style>
        </>
    );
};

export default SMSDoc;
